import React, { useState, useEffect, useRef } from 'react';
import SearchBar from './SearchBar';
import { useIsMobile } from '../hooks/useIsMobile';
import ConfirmModal from './ConfirmModal';
import ActivityTypeFormModal from './ActivityTypeFormModal';
import ActivityCatalogFormModal from './ActivityCatalogFormModal';
import { ActivityType } from '../models/diary';
import {
  activityTypeService,
  activityCatalogService,
  ActivityCatalogEntry,
  ActivityCatalogLevel,
  CreateActivityTypeData,
} from '../services/activityService';

const LEVEL_ORDER: { key: 'low' | 'mid' | 'high'; label: string }[] = [
  { key: 'low', label: 'Bajo' },
  { key: 'mid', label: 'Medio' },
  { key: 'high', label: 'Alto' },
];

const hasConfig = (entry: ActivityCatalogEntry): boolean =>
  !!(entry.levels.low || entry.levels.mid || entry.levels.high);

const LevelChip: React.FC<{ label: string; level: ActivityCatalogLevel | null }> = ({ label, level }) => {
  const configured = !!level;
  return (
    <div style={{
      border: `1px solid ${configured ? '#bae6e3' : '#e5e7eb'}`,
      background: configured ? '#f0fdfa' : '#f9fafb',
      borderRadius: 8, padding: '6px 10px', minWidth: 96,
    }}>
      <div style={{ fontSize: 11, fontWeight: 700, color: configured ? '#0f766e' : '#9ca3af', textTransform: 'uppercase', letterSpacing: 0.3 }}>
        {label}
      </div>
      {configured ? (
        <div style={{ fontSize: 12, color: '#374151', marginTop: 2, whiteSpace: 'nowrap' }}>
          {level!.kcalPerKgMin} kcal · {level!.carbsPercentageDisplay} carbs
        </div>
      ) : (
        <div style={{ fontSize: 12, color: '#9ca3af', marginTop: 2 }}>Sin configurar</div>
      )}
    </div>
  );
};

const ActivityCatalogList: React.FC = () => {
  const [entries, setEntries] = useState<ActivityCatalogEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [activeMenu, setActiveMenu] = useState<number | null>(null);
  const [showTypeCreate, setShowTypeCreate] = useState(false);
  const [editType, setEditType] = useState<ActivityType | undefined>(undefined);
  const [configEntry, setConfigEntry] = useState<ActivityCatalogEntry | undefined>(undefined);
  const [deleteTypeItem, setDeleteTypeItem] = useState<ActivityType | null>(null);
  const [clearConfigEntry, setClearConfigEntry] = useState<ActivityCatalogEntry | null>(null);
  const [deleteError, setDeleteError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const menuRefs = useRef<{ [key: number]: HTMLDivElement | null }>({});
  const isMobile = useIsMobile();

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const clickedOutside = Object.values(menuRefs.current).every(
        ref => !ref || !ref.contains(event.target as Node)
      );
      if (clickedOutside) setActiveMenu(null);
    };
    if (activeMenu) document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [activeMenu]);

  const fetchEntries = async () => {
    try {
      setLoading(true);
      const results = await activityCatalogService.getAll();
      setEntries(results);
    } catch (error) {
      console.error('Failed to fetch activity catalog:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchEntries();
  }, []);

  const showSuccess = (msg: string) => {
    setSuccessMessage(msg);
    setTimeout(() => setSuccessMessage(''), 3000);
  };

  const handleCreateType = async (data: CreateActivityTypeData) => {
    await activityTypeService.create(data);
    setShowTypeCreate(false);
    await fetchEntries();
    showSuccess('Tipo de actividad creado correctamente.');
  };

  const handleEditType = async (data: CreateActivityTypeData) => {
    if (!editType) return;
    await activityTypeService.update(editType.id, data);
    setEditType(undefined);
    await fetchEntries();
    showSuccess('Tipo de actividad actualizado correctamente.');
  };

  const handleSaveConfig = async (levels: Parameters<typeof activityCatalogService.configure>[1]) => {
    if (!configEntry) return;
    await activityCatalogService.configure(configEntry.activityType.id, levels);
    setConfigEntry(undefined);
    await fetchEntries();
    showSuccess('Niveles configurados correctamente.');
  };

  const handleDeleteType = async () => {
    if (!deleteTypeItem) return;
    try {
      await activityTypeService.remove(deleteTypeItem.id);
      setDeleteTypeItem(null);
      setDeleteError('');
      await fetchEntries();
      showSuccess('Tipo de actividad eliminado correctamente.');
    } catch (error: any) {
      const message = error.response?.error || error.message || 'Error al eliminar el tipo de actividad.';
      setDeleteError(message);
      console.error('Failed to delete activity type:', error);
    }
  };

  const handleClearConfig = async () => {
    if (!clearConfigEntry) return;
    try {
      await activityCatalogService.clear(clearConfigEntry.activityType.id);
      setClearConfigEntry(null);
      setDeleteError('');
      await fetchEntries();
      showSuccess('Configuración eliminada correctamente.');
    } catch (error: any) {
      const message = error.response?.error || error.message || 'Error al eliminar la configuración.';
      setDeleteError(message);
      console.error('Failed to clear activity catalog:', error);
    }
  };

  const filtered = search.trim()
    ? entries.filter(e => e.activityType.name.toLowerCase().includes(search.trim().toLowerCase()))
    : entries;

  const thStyle: React.CSSProperties = { padding: '14px 16px', textAlign: 'left', fontWeight: 600, color: '#374151', fontSize: 13 };

  return (
    <div>
      {successMessage && (
        <div style={{
          background: '#dcfce7', color: '#15803d', padding: '12px 16px', borderRadius: 8,
          marginBottom: 16, fontSize: 14, fontWeight: 500, display: 'flex', alignItems: 'center', gap: 8,
        }}>
          <span>&#10003;</span>
          {successMessage}
        </div>
      )}
      <div style={{
        display: 'flex', flexDirection: isMobile ? 'column' : 'row',
        alignItems: isMobile ? 'stretch' : 'center', justifyContent: 'space-between',
        gap: isMobile ? 12 : 0, marginBottom: 24,
      }}>
        <SearchBar value={search} onChange={setSearch} placeholder="Buscar por nombre" fullWidth={isMobile} />
        <button
          onClick={() => setShowTypeCreate(true)}
          style={{
            background: '#38b2ac', color: '#fff', border: 'none', borderRadius: 8,
            padding: '10px 20px', fontSize: 16, fontWeight: 600, cursor: 'pointer',
            display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
            whiteSpace: 'nowrap', boxShadow: '0 2px 4px rgba(56, 178, 172, 0.2)', transition: 'all 0.2s',
          }}
          onMouseEnter={(e) => { e.currentTarget.style.background = '#319795'; e.currentTarget.style.transform = 'translateY(-1px)'; }}
          onMouseLeave={(e) => { e.currentTarget.style.background = '#38b2ac'; e.currentTarget.style.transform = 'translateY(0)'; }}
        >
          <span style={{ fontSize: 20, lineHeight: 1 }}>+</span>
          <span>Nuevo Tipo</span>
        </button>
      </div>

      {loading ? (
        <div style={{ padding: 40, fontSize: 18, color: '#666', textAlign: 'center' }}>Cargando actividades...</div>
      ) : filtered.length === 0 ? (
        <div style={{ padding: 40, fontSize: 18, color: '#666', textAlign: 'center' }}>No se encontraron actividades</div>
      ) : (
        <div style={{ background: '#fff', borderRadius: 12, boxShadow: '0 1px 3px rgba(0,0,0,0.1)', overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ background: '#f8fafc', borderBottom: '1px solid #e2e8f0' }}>
                <th style={thStyle}>NOMBRE</th>
                {!isMobile && <th style={thStyle}>DESCRIPCIÓN</th>}
                <th style={thStyle}>NIVELES DE INTENSIDAD</th>
                <th style={{ ...thStyle, textAlign: 'center' }}>ACCIONES</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((entry, index) => {
                const type = entry.activityType;
                const configured = hasConfig(entry);
                return (
                  <tr
                    key={type.id}
                    style={{ borderBottom: index < filtered.length - 1 ? '1px solid #f1f5f9' : 'none' }}
                    onMouseEnter={(e) => { e.currentTarget.style.background = '#f8fafc'; }}
                    onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; }}
                  >
                    <td style={{ padding: '14px 16px', fontWeight: 600, color: '#1f2937', fontSize: 15 }}>{type.name}</td>
                    {!isMobile && (
                      <td style={{ padding: '14px 16px', color: type.description ? '#6b7280' : '#cbd5e1', fontSize: 14 }}>
                        {type.description || '—'}
                      </td>
                    )}
                    <td style={{ padding: '14px 16px' }}>
                      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                        {LEVEL_ORDER.map(({ key, label }) => (
                          <LevelChip key={key} label={label} level={entry.levels[key]} />
                        ))}
                      </div>
                    </td>
                    <td style={{ padding: '14px 16px', textAlign: 'center' }}>
                      <div ref={(el) => { menuRefs.current[type.id] = el; }} style={{ position: 'relative', display: 'inline-block' }}>
                        <button
                          onClick={(e) => { e.stopPropagation(); setActiveMenu(activeMenu === type.id ? null : type.id); }}
                          style={{
                            background: 'transparent', border: 'none', cursor: 'pointer', padding: '8px',
                            fontSize: '24px', color: '#555', lineHeight: 1, borderRadius: '50%',
                            width: '36px', height: '36px', display: 'inline-flex', alignItems: 'center',
                            justifyContent: 'center', transition: 'background 0.2s',
                          }}
                          onMouseEnter={(e) => { e.currentTarget.style.background = '#f0f0f0'; }}
                          onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; }}
                        >
                          &#8942;
                        </button>
                        {activeMenu === type.id && (
                          <div style={{
                            position: 'absolute', right: 0, top: '100%', marginTop: '4px', background: '#fff',
                            boxShadow: '0 4px 12px rgba(0,0,0,0.15)', borderRadius: '8px', minWidth: '220px',
                            padding: '8px 0', zIndex: 100,
                          }}>
                            <button
                              style={{ width: '100%', padding: '12px 16px', border: 'none', background: 'transparent', textAlign: 'left', cursor: 'pointer', fontSize: '15px', color: '#222' }}
                              onClick={(e) => { e.stopPropagation(); setActiveMenu(null); setConfigEntry(entry); }}
                              onMouseOver={(e) => (e.currentTarget.style.background = '#f5f5f5')}
                              onMouseOut={(e) => (e.currentTarget.style.background = 'transparent')}
                            >
                              {configured ? 'Editar niveles' : 'Configurar niveles'}
                            </button>
                            <button
                              style={{ width: '100%', padding: '12px 16px', border: 'none', background: 'transparent', textAlign: 'left', cursor: 'pointer', fontSize: '15px', color: '#222' }}
                              onClick={(e) => { e.stopPropagation(); setActiveMenu(null); setEditType(type); }}
                              onMouseOver={(e) => (e.currentTarget.style.background = '#f5f5f5')}
                              onMouseOut={(e) => (e.currentTarget.style.background = 'transparent')}
                            >
                              Editar tipo
                            </button>
                            {configured && (
                              <button
                                style={{ width: '100%', padding: '12px 16px', border: 'none', background: 'transparent', textAlign: 'left', cursor: 'pointer', fontSize: '15px', color: '#b45309' }}
                                onClick={(e) => { e.stopPropagation(); setActiveMenu(null); setClearConfigEntry(entry); }}
                                onMouseOver={(e) => (e.currentTarget.style.background = '#fffbeb')}
                                onMouseOut={(e) => (e.currentTarget.style.background = 'transparent')}
                              >
                                Eliminar configuración
                              </button>
                            )}
                            <button
                              style={{ width: '100%', padding: '12px 16px', border: 'none', background: 'transparent', textAlign: 'left', cursor: 'pointer', fontSize: '15px', color: '#e53e3e' }}
                              onClick={(e) => { e.stopPropagation(); setActiveMenu(null); setDeleteTypeItem(type); }}
                              onMouseOver={(e) => (e.currentTarget.style.background = '#fff5f5')}
                              onMouseOut={(e) => (e.currentTarget.style.background = 'transparent')}
                            >
                              Eliminar tipo
                            </button>
                          </div>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* Create/Edit activity type */}
      <ActivityTypeFormModal
        open={showTypeCreate || !!editType}
        item={editType}
        onSave={editType ? handleEditType : handleCreateType}
        onCancel={() => { setShowTypeCreate(false); setEditType(undefined); }}
      />

      {/* Configure intensity levels */}
      <ActivityCatalogFormModal
        open={!!configEntry}
        entry={configEntry}
        onSave={handleSaveConfig}
        onCancel={() => setConfigEntry(undefined)}
      />

      {/* Delete activity type */}
      <ConfirmModal
        open={!!deleteTypeItem}
        title="Eliminar tipo de actividad"
        message={
          <div>
            <span>{`¿Estás seguro de que deseas eliminar "${deleteTypeItem?.name}"? Se eliminará también su configuración de niveles. Esta acción no se puede deshacer.`}</span>
            {deleteError && (
              <div style={{ marginTop: 12, background: '#fee2e2', color: '#dc2626', padding: '10px 14px', borderRadius: 6, fontSize: 14 }}>
                {deleteError}
              </div>
            )}
          </div>
        }
        confirmText="Eliminar"
        cancelText="Cancelar"
        onConfirm={handleDeleteType}
        onCancel={() => { setDeleteTypeItem(null); setDeleteError(''); }}
      />

      {/* Clear level configuration */}
      <ConfirmModal
        open={!!clearConfigEntry}
        title="Eliminar configuración de niveles"
        message={
          <div>
            <span>{`¿Eliminar la configuración de niveles de "${clearConfigEntry?.activityType.name}"? El tipo de actividad se mantendrá.`}</span>
            {deleteError && (
              <div style={{ marginTop: 12, background: '#fee2e2', color: '#dc2626', padding: '10px 14px', borderRadius: 6, fontSize: 14 }}>
                {deleteError}
              </div>
            )}
          </div>
        }
        confirmText="Eliminar configuración"
        cancelText="Cancelar"
        onConfirm={handleClearConfig}
        onCancel={() => { setClearConfigEntry(null); setDeleteError(''); }}
      />
    </div>
  );
};

export default ActivityCatalogList;
